import { useState, type ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import { useAuth } from '@/auth/AuthProvider'
import { NutrientIcon } from '@/components/ui/NutrientIcon'
import { Ionicon } from '@/components/ui/Ionicon'
import { useNutrientColors, type NutrientColorKey } from '@/hooks/use-nutrient-colors'
import { useColorScheme } from '@/hooks/use-color-scheme'
import { withAlpha } from '@/theme/color'
import { addFavoriteFromEntry } from '@/lib/firestore/favorites'
import type { DiaryEntry, DiaryEntryInput, MealTime, Nutrients } from '@/types'
import { getEffectiveOptionalNutrients } from '@/liver/optionalNutrients'

/**
 * Manual entry — web port of mobile components/diary/ManualEntrySheet.tsx:
 * Food Name, Serving Size (amount + unit), the six core nutrients (calories,
 * added sugar, sat fat, fiber, sodium, protein), then the optional groups the
 * user enabled (carbs + sugars, fat + trans fat, cholesterol). Optional values
 * are only written when typed. safetyLevel: 'safe'.
 *
 * Also exports the shared nutrient input row (mobile NutrientInput) used by
 * every manual create/edit surface on the web.
 */

export interface NutrientField {
  key: keyof Nutrients
  labelKey: string
  fb: string
  unit: string
  /** NutrientIcon name (PNG asset — see NutrientIcon). */
  iconKey?: string
  /** Literal emoji mark for nutrients without icon art. */
  emoji?: string
  /** NutrientColors key — resolved per scheme via useNutrientColors(). */
  color: NutrientColorKey
}

/** The liver Core-4 (web callers: recipe editor, saved foods, entry edit). */
export const CORE: NutrientField[] = [
  { key: 'calories', labelKey: 'diary.nutrients.calories', fb: 'Calories', unit: 'kcal', iconKey: 'calories', color: 'calories' },
  { key: 'addedSugarsG', labelKey: 'diary.nutrients.addedSugar', fb: 'Added Sugar', unit: 'g', iconKey: 'addedSugar', color: 'addedSugar' },
  { key: 'saturatedFatG', labelKey: 'diary.nutrients.saturatedFat', fb: 'Sat. Fat', unit: 'g', iconKey: 'saturatedFat', color: 'saturatedFat' },
  { key: 'fiberG', labelKey: 'diary.nutrients.fiber', fb: 'Fiber', unit: 'g', iconKey: 'fiber', color: 'fiber' },
]

export const OPTIONAL: Record<string, NutrientField> = {
  sodium: { key: 'sodiumMg', labelKey: 'diary.nutrients.sodium', fb: 'Sodium', unit: 'mg', iconKey: 'sodium', color: 'sodium' },
  carbs: { key: 'carbsG', labelKey: 'diary.nutrients.carbs', fb: 'Carbs', unit: 'g', iconKey: 'carbs', color: 'carbs' },
  sugars: { key: 'sugarsG', labelKey: 'diary.nutrients.sugars', fb: 'Total Sugars', unit: 'g', iconKey: 'sugars', color: 'sugars' },
  fat: { key: 'fatG', labelKey: 'diary.nutrients.fat', fb: 'Fat', unit: 'g', iconKey: 'fat', color: 'fat' },
  transFat: { key: 'transFatG', labelKey: 'diary.nutrients.transFat', fb: 'Trans Fat', unit: 'g', iconKey: 'transFat', color: 'transFat' },
  cholesterol: { key: 'cholesterolMg', labelKey: 'diary.nutrients.cholesterol', fb: 'Cholesterol', unit: 'mg', iconKey: 'cholesterol', color: 'cholesterol' },
  protein: { key: 'proteinG', labelKey: 'diary.nutrients.protein', fb: 'Protein', unit: 'g', iconKey: 'protein', color: 'protein' },
}

/** Mobile ManualEntrySheet / EditQuickLogItemModal / EditEntrySheet core list (liver axes + targets). */
export const MANUAL_CORE: NutrientField[] = [...CORE, OPTIONAL.sodium, OPTIONAL.protein]

/** Optional groups behind the user's toggles (sodium/protein are core in these forms). */
export function optionalFormFields(opts?: { showCarbs?: boolean; showFat?: boolean; showCholesterol?: boolean }): NutrientField[] {
  return [
    ...(opts?.showCarbs ? [OPTIONAL.carbs, OPTIONAL.sugars] : []),
    ...(opts?.showFat ? [OPTIONAL.fat, OPTIONAL.transFat] : []),
    ...(opts?.showCholesterol ? [OPTIONAL.cholesterol] : []),
  ]
}

export const ML_PER_OZ = 29.5735

/** Section label (mobile styles.sectionLabel: 12 medium, textSecondary, mb 8). */
export function SectionLabel({ children, className = '' }: { children: ReactNode; className?: string }) {
  return <p className={'mb-sm text-[12px] font-medium leading-[normal] text-text-secondary ' + className}>{children}</p>
}

/** The 60px hairline between the core and optional nutrient groups. */
export function OptionalDivider() {
  return <div className="mx-auto my-md h-px w-[60px] bg-border opacity-30" />
}

/** Text field chrome shared by the sheets (radius lg, border, faint fill). */
export function useFieldBg(): string {
  return useColorScheme() === 'dark' ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.03)'
}

function NumberBox({
  value,
  onChange,
  unit,
  borderColor,
  placeholder = '0',
}: {
  value: string
  onChange: (v: string) => void
  unit: string
  borderColor: string
  placeholder?: string
}) {
  const bg = useFieldBg()
  return (
    <div
      className="flex min-w-[120px] items-center rounded-md border px-sm"
      style={{ borderColor, backgroundColor: bg }}
    >
      <input
        type="number"
        inputMode="decimal"
        min="0"
        step="any"
        value={value}
        placeholder={placeholder}
        onChange={(e) => onChange(e.target.value)}
        onFocus={(e) => e.currentTarget.select()}
        className="w-[72px] min-w-0 flex-1 bg-transparent py-xs text-end text-[16px] font-semibold text-text outline-none placeholder:text-text-secondary"
      />
      <span className="ms-xs text-[14px] font-medium text-text-secondary">{unit}</span>
    </div>
  )
}

/**
 * Fluid-content input shared by every manual create/edit surface. Entered in
 * the user's volume unit (mL or oz); callers convert to mL for storage. A
 * value > 0 marks the entry as fluid so it counts toward the fluid tracker.
 */
export function FluidInput({
  value,
  onChange,
  hint = true,
}: {
  value: string
  onChange: (v: string) => void
  hint?: boolean
}) {
  const { t } = useTranslation()
  const { userProfile } = useAuth()
  const nutrientColors = useNutrientColors()
  const isOz = userProfile?.healthProfile?.volumeUnit === 'oz'
  return (
    <div className="flex flex-col">
      <div className="mb-sm flex items-center justify-between">
        <span className="flex min-w-0 flex-1 items-center gap-xs">
          <NutrientIcon nutrientKey="fluid" size={20} style={{ marginInlineEnd: 2 }} />
          <span className="text-[15px] font-medium text-text">{t('diary.nutrients.fluid', 'Fluid')}</span>
        </span>
        <NumberBox value={value} onChange={onChange} unit={isOz ? 'oz' : 'ml'} borderColor={withAlpha(nutrientColors.fluid, 0.25)} />
      </div>
      {hint ? (
        <p className="text-caption text-text-secondary">
          {t('diary.fluids.fluidAmountHint', 'If this is a drink or liquid food (like milk or soup), enter the amount — it counts toward your daily fluid intake.')}
        </p>
      ) : null}
    </div>
  )
}

/** One nutrient row (mobile NutrientInput): art + label, value box tinted by the nutrient hue. */
export function NutrientInput({
  field,
  value,
  onChange,
}: {
  field: NutrientField
  value: string
  onChange: (v: string) => void
}) {
  const { t } = useTranslation()
  const nutrientColors = useNutrientColors()
  return (
    <div className="mb-sm flex items-center justify-between">
      <span className="flex min-w-0 flex-1 items-center gap-xs">
        {field.iconKey ? (
          <NutrientIcon nutrientKey={field.iconKey} size={20} style={{ marginInlineEnd: 2 }} />
        ) : (
          <span aria-hidden className="w-5 text-center">{field.emoji}</span>
        )}
        <span className="truncate text-[15px] font-medium text-text">{t(field.labelKey, field.fb)}</span>
      </span>
      <NumberBox
        value={value}
        onChange={onChange}
        unit={field.unit}
        borderColor={withAlpha(nutrientColors[field.color], 0.25)}
      />
    </div>
  )
}

export function ManualEntryForm({
  meal,
  onAdd,
  onDone,
}: {
  meal: MealTime
  onAdd: (input: DiaryEntryInput) => Promise<string | null>
  onDone: () => void
}) {
  const { t } = useTranslation()
  const { user, userProfile } = useAuth()
  const opts = getEffectiveOptionalNutrients(userProfile?.healthProfile?.condition, userProfile?.healthProfile?.optionalNutrients)
  const isOz = userProfile?.healthProfile?.volumeUnit === 'oz'
  const fieldBg = useFieldBg()

  const [name, setName] = useState('')
  const [servingSize, setServingSize] = useState('1')
  const [servingUnit, setServingUnit] = useState(t('diary.entry.serving', 'serving'))
  const [values, setValues] = useState<Record<string, string>>({})
  const [fluid, setFluid] = useState('')
  const [saveFavorite, setSaveFavorite] = useState(false)
  const [busy, setBusy] = useState(false)

  const set = (key: string) => (v: string) => setValues((p) => ({ ...p, [key]: v }))
  const val = (key: string) => values[key] ?? ''
  const optionalFields = optionalFormFields(opts)
  const isFormValid = name.trim().length > 0

  async function save() {
    if (!isFormValid) return
    setBusy(true)
    try {
      const num = (key: string) => parseFloat(val(key)) || 0
      // Core budgets always stored; optional nutrients only when entered.
      const nutrients: Nutrients = {
        calories: num('calories'),
        addedSugarsG: num('addedSugarsG'),
        saturatedFatG: num('saturatedFatG'),
        fiberG: num('fiberG'),
        sodiumMg: num('sodiumMg'),
        proteinG: num('proteinG'),
      }
      for (const key of ['carbsG', 'sugarsG', 'fatG', 'transFatG', 'cholesterolMg'] as const) {
        if (val(key) !== '') nutrients[key] = num(key)
      }
      // Added sugar is a subset of total sugars — keep sugars consistent.
      if (nutrients.sugarsG === undefined && nutrients.addedSugarsG > 0) {
        nutrients.sugarsG = nutrients.addedSugarsG
      }
      // Web addition: fluid content (user's unit → mL); >0 marks the entry as fluid.
      const fluidMl = Math.round((parseFloat(fluid) || 0) * (isOz ? ML_PER_OZ : 1))
      const input: DiaryEntryInput = {
        date: '',
        mealType: meal,
        name: name.trim(),
        source: 'manual',
        servingSize: parseFloat(servingSize) || 1,
        servingUnit: servingUnit || 'serving',
        servingsConsumed: 1,
        nutrients,
        isFluid: fluidMl > 0,
        ...(fluidMl > 0 ? { fluidMl, fluidType: 'other' as const } : {}),
        safetyLevel: 'safe',
      }
      const id = await onAdd(input)
      // Web addition: one-tap "Save to Favorites".
      if (saveFavorite && id && user) {
        await addFavoriteFromEntry(user.uid, {
          ...input,
          id,
          userId: user.uid,
          timestamp: new Date(),
        } as DiaryEntry).catch(() => {})
      }
      onDone()
    } finally {
      setBusy(false)
    }
  }

  const box = 'rounded-lg border border-border px-md'

  return (
    <div className="flex flex-col">
      {/* Food name */}
      <SectionLabel>{t('diary.manualEntry.foodName', 'Food Name')}</SectionLabel>
      <div className={box} style={{ backgroundColor: fieldBg }}>
        <input
          autoFocus
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder={t('diary.manualEntry.namePlaceholder', 'e.g., Grilled Chicken')}
          autoCapitalize="words"
          className="w-full bg-transparent py-sm text-[16px] font-medium text-text outline-none placeholder:text-text-secondary"
        />
      </div>

      {/* Serving size */}
      <SectionLabel className="mt-lg">{t('diary.entry.servingSize', 'Serving Size')}</SectionLabel>
      <div className="flex gap-sm">
        <div className={box + ' flex-1'} style={{ backgroundColor: fieldBg }}>
          <input
            type="number"
            inputMode="decimal"
            min="0.01"
            step="any"
            value={servingSize}
            onChange={(e) => setServingSize(e.target.value)}
            onFocus={(e) => e.currentTarget.select()}
            className="w-full bg-transparent py-sm text-center text-[18px] font-semibold text-text outline-none"
          />
        </div>
        <div className={box + ' flex-[1.5]'} style={{ backgroundColor: fieldBg }}>
          <input
            value={servingUnit}
            onChange={(e) => setServingUnit(e.target.value)}
            placeholder={t('diary.entry.unitPlaceholder', 'unit')}
            className="w-full bg-transparent py-sm text-[16px] font-medium text-text outline-none placeholder:text-text-secondary"
          />
        </div>
      </div>

      {/* Nutrients */}
      <SectionLabel className="mt-lg">{t('diary.entry.nutrients', 'Nutrients')}</SectionLabel>
      {MANUAL_CORE.map((f) => (
        <NutrientInput key={f.key} field={f} value={val(f.key as string)} onChange={set(f.key as string)} />
      ))}

      {optionalFields.length > 0 ? (
        <>
          <OptionalDivider />
          {optionalFields.map((f) => (
            <NutrientInput key={f.key} field={f} value={val(f.key as string)} onChange={set(f.key as string)} />
          ))}
        </>
      ) : null}

      {/* Web additions: fluid content + save as favorite */}
      <OptionalDivider />
      <FluidInput value={fluid} onChange={setFluid} />
      <label className="mt-md flex cursor-pointer items-center gap-sm text-[15px] font-medium text-text">
        <input
          type="checkbox"
          checked={saveFavorite}
          onChange={(e) => setSaveFavorite(e.target.checked)}
          className="h-4 w-4 accent-primary"
        />
        <Ionicon name={saveFavorite ? 'heart' : 'heart-outline'} size={18} className="text-primary" />
        {t('diary.entry.saveToFavorites', 'Save to Favorites')}
      </label>

      {/* Action button */}
      <button
        type="button"
        onClick={() => void save()}
        disabled={busy || !isFormValid}
        className={
          'mt-xl flex w-full items-center justify-center gap-sm rounded-lg py-md ' +
          (isFormValid ? 'bg-button-primary-strong text-white' : 'bg-button-disabled text-white') +
          (busy ? ' opacity-60' : '')
        }
      >
        <Ionicon name="add-circle" size={22} />
        <span className="text-[18px] font-semibold leading-[normal]">{t('diary.manualEntry.addEntry', 'Add to Diary')}</span>
      </button>
    </div>
  )
}
